package mapsearch.users

import apiclient.geoobjects.MarkerColor
import apiclient.geoobjects.MarkerIcon
import apiclient.geoobjects.MarkerShape
import apiclient.geoobjects.ObjectType
import apiclient.geoobjects.toLatLon
import auth.ApiUserStore
import com.jillesvangurp.geojson.centroid
import data.objects.ActiveObjectStore
import data.users.UserListStore
import dev.fritz2.core.RootStore
import dev.fritz2.core.SimpleHandler
import koin.koinCtx
import koin.withKoin
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.seconds
import kotlinx.coroutines.CoroutineName
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import location.LocationUploadStore
import maplibreGL.FormationMap
import maplibreGL.MaplibreMarkerOptions
import maplibreGL.toJs
import maplibreGL.toLngLat
import mapsearch.MarkerGenerators.makeSvgMarkerForMap
import mapsearch.userMarkerEventListener
import mu.KotlinLogging
import svgmarker.myUserSize
import svgmarker.userSvgIconOptions

private val log = KotlinLogging.logger("UserMapMarkerStore")

class UserMapMarkerStore :
    RootStore<Map<String, Pair<apiclient.mapsearch.UserMarker, maplibreGL.Marker>>?>(null, Job()) {

    private val apiUserStore: ApiUserStore by koinCtx.inject()

    fun isUserSharing(userId: String): Boolean {
        val locationUploadStore: LocationUploadStore by koinCtx.inject()
        return when (userId) {
            apiUserStore.current.userId -> {
                locationUploadStore.current.isActive
            }

            else -> current?.get(userId) != null
        }
    }

    private fun createUserMapMarker(userMarker: apiclient.mapsearch.UserMarker): maplibreGL.Marker? {
        withKoin {
            val userListStore: UserListStore = get()
            val activeObjectStore: ActiveObjectStore = get()

            val userProfile = userListStore.getPublicUserProfile(userMarker.userId)
            // lookup user
            return if (userProfile != null) {
                userMarker.geometry?.centroid()?.toLatLon()?.toLngLat()?.let { lngLat ->
                    log.info { "[Websocket] Create UserMapMarker for ${userProfile.name} (${userMarker.userId})" }
                    val userSharingState = isUserSharing(userMarker.userId)
                    val userPicture = userListStore.getPublicUserProfile(userMarker.userId)?.profilePhoto?.href
                    val markerOptions = MaplibreMarkerOptions(
                        element = makeSvgMarkerForMap(
                            id = userMarker.userId,
                            title = userProfile.name,
                            svgIconOptions = userSvgIconOptions(
                                size = myUserSize,
                                color = MarkerColor.Default,
                                icon = MarkerIcon.User,
                                shape = MarkerShape.Circle,
                                hasNotification = false,
                                archived = false,
                                flagged = false,
                                desaturated = false,
                                highlighted = false,
                                sharing = userSharingState,
                                picture = userPicture,
                                bitmapImage = null,
                            ),
                            objectType = ObjectType.UserMarker,
                            active = activeObjectStore.current.id == userMarker.userId,
                            showTitle = true,
                        ).also { element ->
//                            if (!svgIconOptions.desaturated) { // make non-clickable when desaturated (= disabled)
                            element.addEventListener("click", userMarkerEventListener)
//                            }
                        },
                    )
                    val newMarker = maplibreGL.Marker(markerOptions.toJs())
                    newMarker.setLngLat(lngLat)
                    newMarker
                }
            } else null
        }
    }

    val handleUserUpdates = SimpleHandler<List<apiclient.mapsearch.UserMarker>> { data, _ ->
        data handledBy { userUpdates ->
            withKoin {
                val formationMap: FormationMap = get()
                val map = formationMap.map
                val apiUserStore: ApiUserStore by inject()
                val me = apiUserStore.current.userId
//                log.info {
//                    "[Websocket] Sharing Users: ${userUpdates.size} -> ${
//                        userUpdates.map {
//                            it.userId + if (it.userId == me) " (you)" else ""
//                        }
//                    }"
//                }
                val mutable = current.orEmpty().toMutableMap()
                userUpdates.forEach { userUpdate ->
                    if (userUpdate.userId != me) {
                        log.info { "[Websocket] Update User Position for ${userUpdate.userId} @ ${userUpdate.updatedAt}" }
                        // update position and timeStamp if marker exists
                        mutable[userUpdate.userId]?.let { (userMarker, mapMarker) ->
                            val updatedUserMarker = userMarker.copy(
                                geometry = userUpdate.geometry,
                                updatedAt = userUpdate.updatedAt,
                            )
                            val lngLat = userUpdate.geometry?.centroid()?.toLatLon()?.toLngLat()
                            if (lngLat != null) {
                                mapMarker.setLngLat(lngLat)
                            }
                            mutable[userUpdate.userId] = Pair(updatedUserMarker, mapMarker)
                        } ?: run {
                            // create marker if marker not exists
                            createUserMapMarker(userUpdate)?.let { mapMarker ->
                                mapMarker.addTo(map)
                                mutable[userUpdate.userId] = Pair(userUpdate, mapMarker)
                            }
                        }
                    }
                    update(mutable)
                }
            }
        }
    }

    val clearOut = handle { markers ->
        if (!markers.isNullOrEmpty()) {
            val oldSize = markers.size
            val clearedOutList = markers.mapNotNull { (userId, marker) ->
                val (userMarker, mapMarker) = marker
                if ((Clock.System.now() - userMarker.updatedAt) < 10.seconds) {
                    Pair(userId, marker)
                } else {
                    mapMarker.remove()
                    log.info { "[Websocket] Remove UserMapMarker for ${userMarker.userId}" }
                    null
                }
            }.toMap()
            log.info { "[Websocket] Cleared out ${oldSize - clearedOutList.size} UserMapMarker(s)." }
            clearedOutList
        } else markers
    }

    init {
        CoroutineScope(CoroutineName("clear-out-old-markers")).launch {
            while (true) {
                delay(1.minutes)
//                delay(5.seconds)
                clearOut(Unit)
            }
        }
    }
}
